/**
 * Easing feature module (W2-05 easing-templates-raw; DESIGN.md §4.12, §4.13, §5.7; WORKPACKAGES Appendix B:
 * `easing.*`, `templates.*`).
 *
 * Provides:
 *  - `DIALOG.easingPicker` (popover), `DIALOG.bezierEditor` (modal), `DIALOG.templates` (sheet);
 *  - commands: curve copy / paste, "Ease: ..." palette entries for every curve, the Bezier editor, clearing recent
 *    curves, the templates dialog (and one palette entry per template);
 *  - settings rows: easing favourites / recents (`ext.easing.favourites`, with recents) and "always show RPE
 *    numbers" (`ext.easing.showIds`);
 *  - help topics about curves and templates.
 * No default keys: the picker opens from the motion columns (Enter / C, W2-04a), the inspector and the curve editor;
 * the curve commands live in the motion and curve context menus and the palette.
 */

import { DIALOG } from '../../app/dialogs/types';
import type { FeatureModule, HelpTopicDef, SettingDef } from '../../app/registry/types';
import { BezierEditorDialog } from './BezierEditor';
import { m } from './easing.messages';
import { EASING_COMMANDS } from './easingCommands';
import { EasingPicker } from './EasingPicker';
import { FavouritesSetting } from './FavouritesSetting';
import { TemplatesDialog } from './TemplatesDialog';

const SETTINGS: readonly SettingDef[] = [
  {
    path: 'ext.easing.favourites',
    title: m.settingFavourites,
    description: m.settingFavouritesHelp,
    level: 'guided',
    kind: 'custom',
    component: FavouritesSetting,
  },
  {
    path: 'ext.easing.showIds',
    title: m.settingShowIds,
    description: m.settingShowIdsHelp,
    level: 'guided',
    kind: 'boolean',
  },
];

const HELP: readonly HelpTopicDef[] = [
  { id: 'easing', title: m.helpTitle, body: m.helpBody, level: 'guided' },
  { id: 'templates', title: m.helpTemplatesTitle, body: m.helpTemplatesBody, level: 'guided' },
];

export default {
  id: 'easing',
  commands: EASING_COMMANDS,
  dialogs: [
    { id: DIALOG.easingPicker, kind: 'popover', component: EasingPicker },
    { id: DIALOG.bezierEditor, kind: 'modal', component: BezierEditorDialog },
    // A non-modal side sheet (UX-040): the preview it loops stays visible. Escape from anywhere closes it through the
    // batch module's tools.closeSheet (one tool sheet at a time).
    { id: DIALOG.templates, kind: 'sheet', component: TemplatesDialog },
  ],
  settings: SETTINGS,
  helpTopics: HELP,
} satisfies FeatureModule;
